import { useParams, useNavigate, Link } from 'react-router-dom'
import { mockRepositories } from '../data/mockRepositories'
import './Page.css'
import './VersionView.css'

function VersionView() {
  const { id } = useParams()
  const navigate = useNavigate()
  
  // Mock version data - in real app, this would come from API
  // For now, we'll create mock data based on repository
  const versionId = id
  
  // Find repository that might have this version (simplified for now)
  const repository = mockRepositories[0] // Using first repo for mock data
  
  // Mock version data
  const version = {
    id: versionId,
    version_number: 'v1.2.0',
    repository_id: repository?.id,
    repository_slug: repository?.slug,
    repository_title: repository?.title,
    commit_id: 'commit-abc123',
    commit_hash: 'abc1234567890',
    commit_message: 'Add Silverface revision schematic and update component values',
    author: {
      id: repository?.owner.id,
      username: repository?.owner.username
    },
    changelog: `## Version 1.2.0

### Added
- Silverface revision schematic
- Component value annotations
- Wiring diagram for reissue version

### Changed
- Updated main schematic with corrected component values
- Improved documentation for tube bias adjustments

### Fixed
- Corrected resistor values in reverb circuit
- Fixed typo in component reference designators`,
    status: 'published',
    validation_status: 'verified',
    published_at: '2024-12-15T10:30:00Z',
    created_at: '2024-12-10T09:00:00Z',
    files: [
      {
        id: 'file-1',
        name: 'schematic.svg',
        path: 'schematics/main-schematic.svg',
        size: 245760,
        mime_type: 'image/svg+xml',
        description: 'Main schematic diagram'
      },
      {
        id: 'file-2',
        name: 'component-layout.pdf',
        path: 'docs/component-layout.pdf',
        size: 512000,
        mime_type: 'application/pdf',
        description: 'Component layout and PCB guide'
      },
      {
        id: 'file-3',
        name: 'wiring-diagram.pdf',
        path: 'docs/wiring-diagram.pdf',
        size: 384000,
        mime_type: 'application/pdf',
        description: 'Internal wiring diagram'
      },
      {
        id: 'file-4',
        name: 'readme.md',
        path: 'README.md',
        size: 8192,
        mime_type: 'text/markdown',
        description: 'Repository README'
      }
    ]
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const handleDownload = (file) => {
    // Placeholder for download functionality
    console.log('Download file:', file.name)
    // In real app: window.open(`/api/files/${file.id}/download`)
  }

  const handleViewFile = (file) => {
    // Placeholder for file viewer
    console.log('View file:', file.name)
    // In real app: navigate to file viewer or open in new tab
  }

  if (!version) {
    return (
      <div className="page-container">
        <div className="empty-state">
          <h1>Version Not Found</h1>
          <p>The version you&rsquo;re looking for doesn&rsquo;t exist.</p>
          <Link to="/repositories" className="back-link">
            ← Back to Browse Repositories
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="page-container version-view">
      {/* Header Navigation */}
      <div className="version-header-nav">
        <button onClick={() => navigate(-1)} className="back-button">
          ← Back
        </button>
        <Link to={`/repositories/${version.repository_slug}`} className="repo-link">
          {version.repository_title}
        </Link>
        <span className="nav-separator">/</span>
        <Link to={`/repositories/${version.repository_slug}/versions`} className="versions-link">
          Versions
        </Link>
      </div>

      {/* Version Header */}
      <div className="version-header">
        <div className="version-title-section">
          <h1>Version {version.version_number}</h1>
          <div className="version-badges">
            <span className={`status-badge status-${version.status}`}>
              {version.status}
            </span>
            <span className={`validation-badge validation-${version.validation_status}`}>
              {version.validation_status}
            </span>
          </div>
        </div>
        
        <div className="version-meta">
          <div className="version-meta-item">
            <span className="meta-label">Author</span>
            <Link to={`/users/${version.author.id}`} className="meta-value link">
              {version.author.username}
            </Link>
          </div>
          <div className="version-meta-item">
            <span className="meta-label">Published</span>
            <span className="meta-value">{formatDate(version.published_at)}</span>
          </div>
          <div className="version-meta-item">
            <span className="meta-label">Commit</span>
            <Link to={`/commits/${version.commit_id}`} className="meta-value link commit-hash">
              {version.commit_hash.slice(0, 7)}
            </Link>
          </div>
        </div>
      </div>

      {/* Commit Message */}
      <div className="commit-message-box">
        <div className="commit-header">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M0 6.75C0 5.784.784 5 1.75 5h1.5a.75.75 0 010 1.5h-1.5a.25.25 0 00-.25.25v7.5c0 .138.112.25.25.25h7.5a.25.25 0 00.25-.25v-1.5a.75.75 0 011.5 0v1.5A1.75 1.75 0 0110.25 15h-7.5A1.75 1.75 0 011 13.25v-7.5z"/>
            <path d="M5 1.75C5 .784 5.784 0 6.75 0h7.5C15.216 0 16 .784 16 1.75v7.5A1.75 1.75 0 0114.25 11h-7.5A1.75 1.75 0 015 9.25v-7.5zm1.75-.25a.25.25 0 00-.25.25v7.5c0 .138.112.25.25.25h7.5a.25.25 0 00.25-.25v-7.5a.25.25 0 00-.25-.25h-7.5z"/>
          </svg>
          <span className="commit-label">Commit</span>
        </div>
        <p className="commit-message-text">{version.commit_message}</p>
      </div>

      {/* Main Content Layout */}
      <div className="version-content-layout">
        {/* Main Content */}
        <div className="version-main-content">
          {/* Changelog Section */}
          {version.changelog && (
            <section className="version-section">
              <h2>Changelog</h2>
              <div className="changelog-content">
                <pre className="changelog-text">{version.changelog}</pre>
              </div>
            </section>
          )}

          {/* Files Section */}
          <section className="version-section">
            <div className="section-header">
              <h2>Files</h2>
              <button className="download-all-button">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M8 0a.75.75 0 01.75.75v5.5h5.5a.75.75 0 010 1.5h-5.5v5.5a.75.75 0 01-1.5 0v-5.5H2.75a.75.75 0 010-1.5h5.5v-5.5A.75.75 0 018 0z"/>
                </svg>
                Download All
              </button>
            </div>
            
            <div className="files-list">
              {version.files.map(file => (
                <div key={file.id} className="file-item">
                  <div className="file-icon">
                    {file.mime_type?.startsWith('image/') ? (
                      <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M2.75 3.5a.25.25 0 00-.25.25v8.5c0 .138.112.25.25.25h10.5a.25.25 0 00.25-.25v-8.5a.25.25 0 00-.25-.25H2.75zM1 3.75C1 2.784 1.784 2 2.75 2h10.5c.966 0 1.75.784 1.75 1.75v8.5A1.75 1.75 0 0113.25 14H2.75A1.75 1.75 0 011 12.25v-8.5zm6 5.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zm4.5-.75a.75.75 0 11-1.5 0 .75.75 0 011.5 0z"/>
                      </svg>
                    ) : file.mime_type === 'application/pdf' ? (
                      <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M3.75 1.5a.25.25 0 00-.25.25v12.5c0 .138.112.25.25.25h9.5a.25.25 0 00.25-.25V6H9.75A1.75 1.75 0 018 4.25V1.5H3.75zm5.75.56v2.19c0 .138.112.25.25.25h2.19L9.5 2.06zM2 1.75C2 .784 2.784 0 3.75 0h5.086c.464 0 .909.184 1.237.513l3.414 3.414c.329.328.513.773.513 1.237v9.586A1.75 1.75 0 0113.25 16h-9.5A1.75 1.75 0 012 14.25V1.75z"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M4.72 3.22a.75.75 0 011.06 1.06L2.06 8l3.72 3.72a.75.75 0 11-1.06 1.06L.47 8.53a.75.75 0 010-1.06l4.25-4.25zm6.56 0a.75.75 0 10-1.06 1.06L13.94 8l-3.72 3.72a.75.75 0 101.06 1.06L15.53 8.53a.75.75 0 000-1.06l-4.25-4.25z"/>
                      </svg>
                    )}
                  </div>
                  
                  <div className="file-info">
                    <div className="file-name-row">
                      <button 
                        className="file-name-button"
                        onClick={() => handleViewFile(file)}
                      >
                        {file.name}
                      </button>
                      <span className="file-path">{file.path}</span>
                    </div>
                    {file.description && (
                      <p className="file-description">{file.description}</p>
                    )}
                    <div className="file-meta">
                      <span className="file-size">{formatFileSize(file.size)}</span>
                      <span className="file-type">{file.mime_type}</span>
                    </div>
                  </div>
                  
                  <div className="file-actions">
                    <button 
                      className="file-action-button"
                      onClick={() => handleViewFile(file)}
                      title="View file"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M8 2.25a.75.75 0 01.75.75v2.5a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM2.25 8a.75.75 0 01.75-.75h2.5a.75.75 0 010 1.5H3a.75.75 0 01-.75-.75zM11 2.25a.75.75 0 01.75.75v2.5a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM13.75 8a.75.75 0 01-.75-.75h-2.5a.75.75 0 010-1.5H13a.75.75 0 01.75.75zM8 11a.75.75 0 01.75.75v2.5a.75.75 0 01-1.5 0v-2.5A.75.75 0 018 11zM3 13.75a.75.75 0 01-.75-.75v-2.5a.75.75 0 011.5 0V13a.75.75 0 01-.75.75zM13 13.75a.75.75 0 01-.75-.75v-2.5a.75.75 0 011.5 0V13a.75.75 0 01-.75.75z"/>
                      </svg>
                      View
                    </button>
                    <button 
                      className="file-action-button"
                      onClick={() => handleDownload(file)}
                      title="Download file"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M8.75 1.5a.75.75 0 00-1.5 0v7.69L5.22 6.72a.75.75 0 00-1.06 1.06l3.5 3.5a.75.75 0 001.06 0l3.5-3.5a.75.75 0 10-1.06-1.06l-2.03 2.47V1.5zM3.75 13a.75.75 0 000 1.5h8.5a.75.75 0 000-1.5h-8.5z"/>
                      </svg>
                      Download
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Sidebar */}
        <aside className="version-sidebar">
          <div className="sidebar-card">
            <h3>Version Information</h3>
            <dl className="version-info-list">
              <div className="info-row">
                <dt>Version</dt>
                <dd>{version.version_number}</dd>
              </div>
              <div className="info-row">
                <dt>Status</dt>
                <dd>
                  <span className={`status-badge status-${version.status}`}>
                    {version.status}
                  </span>
                </dd>
              </div>
              <div className="info-row">
                <dt>Validation</dt>
                <dd>
                  <span className={`validation-badge validation-${version.validation_status}`}>
                    {version.validation_status}
                  </span>
                </dd>
              </div>
              <div className="info-row">
                <dt>Published</dt>
                <dd>{formatDate(version.published_at)}</dd>
              </div>
              <div className="info-row">
                <dt>Created</dt>
                <dd>{formatDate(version.created_at)}</dd>
              </div>
            </dl>
          </div>

          <div className="sidebar-card">
            <h3>Actions</h3>
            <div className="sidebar-actions">
              <Link to={`/repositories/${version.repository_slug}`} className="action-button">
                View Repository
              </Link>
              <button className="action-button secondary">
                Compare Versions
              </button>
              <button className="action-button secondary">
                Download ZIP
              </button>
            </div>
          </div>

          <div className="sidebar-card">
            <h3>Statistics</h3>
            <dl className="version-info-list">
              <div className="info-row">
                <dt>Files</dt>
                <dd>{version.files.length}</dd>
              </div>
              <div className="info-row">
                <dt>Total Size</dt>
                <dd>{formatFileSize(version.files.reduce((sum, f) => sum + f.size, 0))}</dd>
              </div>
            </dl>
          </div>
        </aside>
      </div>
    </div>
  )
}

export default VersionView
